<script setup lang="ts">
import type { CmsElementBioCircle } from "~/composables/useCustom";
import { getProductsById } from "~/server/customAPI";
import SwListingProductPrice from "~/components/product/SwListingProductPrice.vue";
import SharedHeadline from "~/components/shared/SharedHeadline.vue";
import SharedTopline from "~/components/shared/SharedTopline.vue";
import { getProductRoute } from "@shopware/helpers";

const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

const props = defineProps<{
  content: CmsElementBioCircle;
}>();

const {
  headlineText,
  toplineText,
  toplineType,
  headlineType,
  button,
  imageHeadlineText,
  imageMainText,
  imageToplineText,
  mainProductOne,
  mainProductTwo,
  rightImage
} = props.content?.data

const searchResults = ref([]);
searchResults.value = await getProductsById({
  ids: [mainProductOne, mainProductTwo],
});
</script>

<template>
  <section>
    <div class="container max-w-[90rem] px-0 xl:px-5">
      <article
        class="bg-skin-white rounded py-10 md:py-16 prose-p:text-skin-secondary"
      >
        <div class="container">
          <div class="flex flex-wrap gap-y-9 -mx-15">
            <div class="w-full laptop:w-2/6 px-15">
              <SharedTopline
                :headline="toplineText"
                :headlineType="toplineType"
              ></SharedTopline>
              <SharedHeadline
                :headline="headlineText"
                :headlineType="headlineType"
                class="mb-[1.875rem]"
              ></SharedHeadline>
              <div class="grid grid-cols-2 sm:grid-cols-2 gap-5 text-center">
                <div
                  v-for="product in searchResults.elements"
                  :key="product.id"
                >
                  <div
                    class="w-full h-[12.75rem] mx-auto mb-5 text-center grid place-items-center"
                  >
                    <NuxtLink :to="formatLink(getProductRoute(product))">
                      <NuxtImg
                        v-if="product.cover"
                        loading="lazy"
                        format="webp"
                        height="204"
                        class="object-contain h-[12.75rem]"
                        :src="product.cover.media.url"
                        alt=""
                      />
                    </NuxtLink>
                  </div>
                  <p class="text-sm !mb-3">
                    {{ $t("product.article.nr") }}.:{{ product.productNumber }}
                  </p>
                  <h6 class="font-bold text-base mb-3">
                    {{ product.translated.name }}
                  </h6>

                  <h6 class="font-bold text-sm mb-3">
                    <SwListingProductPrice
                      :product="product"
                      class=""
                      data-testid="product-box-product-price"
                    />
                  </h6>
                </div>
              </div>
              <div class="text-center mt-10">
                <SharedButton
                  v-if="button"
                  btn-type="btn"
                  :button="button"
                  class="btn btn-primary"
                />
              </div>
            </div>
            <div class="w-full laptop:w-4/6 px-15 laptop:pl-24">
              <figure class="rounded-10 min-h-[32.6875rem] relative flex">
                <NuxtImg v-if="rightImage?.media?.url" loading="lazy" format="webp" height="204" class="w-full object-cover rounded-10" :src="rightImage?.media?.url" alt="" />
                <img v-else src="/images/img7.jpg" alt="" class="w-full object-cover rounded-10">
                <div class="w-full h-full flex absolute top-0 left-0 z-10 flex-col justify-center p-4 laptop:p-8 laptop:px-[4.7rem] [&>*]:text-skin-white prose-p:text-sm">
                  <h6 class="sub-title" v-html="imageToplineText"></h6>
                  <h2
                    class="section-title leading-[32px] xl:leading-[40px] mb-[1.875rem]"
                    v-html="imageHeadlineText"
                  ></h2>
                  <div
                    class="prose-p:text-white"
                    v-html="
                      imageMainText.replace(
                        '124c71d524604ccbad6042edce3ac799',
                        ''
                      )
                    "
                  ></div>
                </div>
              </figure>
            </div>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>
